import { Injectable } from "@angular/core";
import { AngularFire } from "angularfire2";
import { DateTime } from "../helpers/datetime";
import { each, differenceBy, kebabCase, values } from "lodash";
import moment, { Moment } from "moment";

@Injectable()
export class StatsService {
  constructor(public af: AngularFire) {}

  // AGGREGATE LEVEL USAGE
  // Track total deleted posts
  trackTotalNumberOfDeletedPosts() {
    return this.incrementStatByRef(
      `aggregate-level-usage/deleted-posts/total-deleted-posts`,
    );
  }

  // Track total blocked posts
  trackTotalNumberOfBlockedPosts(post: any) {
    if (post.blocked && post.blocked !== "hasbeenblocked") {
      return this.decrementStatByRef(
        `aggregate-level-usage/blocked-posts/total-blocked-posts`,
      );
    }

    return this.incrementStatByRef(
      `aggregate-level-usage/blocked-posts/total-blocked-posts`,
    );
  }

  // Track total block comments
  trackTotalNumberOfBlockedComments(comment: any) {
    if (comment.blocked && comment.blocked !== "hasbeenblocked") {
      return this.decrementStatByRef(
        `aggregate-level-usage/blocked-comments/total-blocked-comments`,
      );
    }

    return this.incrementStatByRef(
      `aggregate-level-usage/blocked-comments/total-blocked-comments`,
    );
  }

  // Track ranked list of blocked posts
  trackRankedListOfBlockedPosts(post: any) {
    if (post.blocked && post.blocked !== "hasbeenblocked") {
      return this.decrementStatByRef(
        `aggregate-level-usage/blocked-posts/posts/${post.id}`,
      );
    }

    return this.incrementStatByRef(
      `aggregate-level-usage/blocked-posts/posts/${post.id}`,
    );
  }

  // Track ranked list of blocked comments
  trackRankedListOfBlockedComments(comments: any) {
    if (comments.blocked && comments.blocked !== "hasbeenblocked") {
      return this.decrementStatByRef(
        `aggregate-level-usage/blocked-comments/comments/${comments.id}`,
      );
    }

    return this.incrementStatByRef(
      `aggregate-level-usage/blocked-comments/comments/${comments.id}`,
    );
  }

  // Track total reported posts
  trackTotalNumberOfReportedPosts() {
    return this.incrementStatByRef(
      `aggregate-level-usage/reported-posts/total-reported-posts`,
    );
  }

  // Track ranked list of reported posts
  trackRankedListOfReportedPosts(post: any) {
    return this.incrementStatByRef(
      `aggregate-level-usage/reported-posts/posts/${post.id}`,
    );
  }

  // Track ranked list of relationship health
  trackRankedListOfRelationshipHealth(user: any, oldUser?: any) {
    let oldUserHealth = oldUser && oldUser.health ? oldUser.health : null;

    return this.trackRankedAttributeDiffStat(
      "aggregate-level-usage",
      "relationship-health",
      "level",
      user.health,
      oldUserHealth,
      true,
      false,
    );
  }

  // Track ranked list of relationship values
  trackRankedListOfRelationshipValues(user: any, oldUser?: any) {
    let attributes = null;

    if (oldUser && oldUser.values) {
      attributes = differenceBy(
        values(oldUser.values),
        values(user.values),
        "id",
      );
    } else {
      attributes = values(user.values);
    }

    return this.trackRankedAttributeStats(
      "aggregate-level-usage",
      "relationship-values",
      attributes,
      true,
      false,
    );
  }

  // Track ranked list of post values
  trackRankedListOfSafePeopleInviteTypes(inviteType: string) {
    return this.trackRankedAttributeStat(
      "aggregate-level-usage/safe-people",
      "invite-types",
      inviteType,
      false,
      false,
    );
  }

  // Track ranked list of post values
  trackRankedListOfPostValues(values?: any[]) {
    return this.trackRankedAttributeStats(
      "aggregate-level-usage/posts",
      "post-values",
      values,
      true,
      false,
    );
  }

  // Track ranked list of post emotion
  trackRankedListOfPostEmotion(emotion?: any) {
    return this.trackRankedAttributeStat(
      "aggregate-level-usage/posts",
      "post-emotions",
      emotion,
      true,
      false,
    );
  }

  // Track ranked list of post health
  trackRankedListOfPostHealth(health?: any) {
    return this.trackRankedAttributeStat(
      "aggregate-level-usage/posts",
      "post-health",
      health,
      true,
      false,
    );
  }

  // Track ranked list of search queries
  trackRankedListOfSearchesMade(searchTerm: string) {
    searchTerm = kebabCase(searchTerm);

    return this.trackRankedAttributeStat(
      "aggregate-level-usage/search",
      "search-terms",
      searchTerm,
      false,
    );
  }

  // Track ranked list of articles accessed
  trackRankedListOfArticlesAccessed(post: any) {
    return this.trackRankedAttributeStat(
      "aggregate-level-usage/articles",
      "total-accessed",
      post.id,
      false,
      false,
    );
  }

  // Track ranked list of articles favourited
  trackRankedListOfArticlesFavourited(post: any) {
    return this.trackRankedAttributeStat(
      "aggregate-level-usage/articles",
      "total-favourited",
      post.id,
      false,
      false,
    );
  }

  // USER LEVEL USAGE
  // Track total user searches
  trackReportedPosts(user: any, post: any) {
    return Promise.all([
      this.trackTotalNumberOfReportedPosts(),
      this.trackRankedListOfReportedPosts(post),
      this.trackTotalNumberOfReportedPostsByUser(user),
      this.trackRankedListOfReportedPostsByUser(user, post),
    ]);
  }

  // Track total reported posts by user
  trackTotalNumberOfReportedPostsByUser(user: any) {
    return this.incrementStatByRef(
      `user-level-usage/${user.id}/reported-posts/total-reported-posts`,
    );
  }

  // Track ranked list of reported posts by user
  trackRankedListOfReportedPostsByUser(user: any, post: any) {
    return this.incrementStatByRef(
      `user-level-usage/${user.id}/reported-posts/posts/${post.id}`,
    );
  }

  // Track total user searches
  trackTotalNumberOfSearchesMade(user: any) {
    return this.incrementStatByRef(
      `user-level-usage/${user.id}/search/total-searches`,
    );
  }

  // Track total monthly user searches
  trackTotalNumberOfMonthlySearchesMade(user: any, dateTime?: Moment) {
    return this.trackMonthlyStatTotalByRef(
      `user-level-usage/${user.id}/search`,
      "total-searches",
      dateTime,
    );
  }

  // Track average monthly user searches
  trackAverageNumberOfMonthlySearchesMade(
    user: any,
    totalMonthlySearches: number,
    dateTime?: Moment,
  ) {
    return this.trackMonthlyStatAverageByRef(
      `user-level-usage/${user.id}/safe-people`,
      "average-searches",
      totalMonthlySearches,
      dateTime,
    );
  }

  // Track total nudges
  trackTotalNumberOfNudgesMade(user: any, post: any) {
    return Promise.all([
      this.incrementStatByRef(
        `user-level-usage/${user.id}/posts/post-nudges/total-nudges-made`,
      ),
      this.incrementStatByRef(
        `user-level-usage/${user.id}/posts/post-nudges/total-nudges-by-post/${
          post.id
        }`,
      ),
    ]);
  }

  // Track total comments
  trackTotalNumberOfCommentsMade(user: any, post: any) {
    return Promise.all([
      this.incrementStatByRef(
        `user-level-usage/${user.id}/posts/post-comments/total-comments-made`,
      ),
      this.incrementStatByRef(
        `user-level-usage/${
          user.id
        }/posts/post-comments/total-comments-by-post/${post.id}`,
      ),
    ]);
  }

  // Track total monthly comments
  trackTotalNumberOfMonthlyCommentsMade(user: any, dateTime?: Moment) {
    return this.trackMonthlyStatTotalByRef(
      `user-level-usage/${user.id}/posts/post-comments`,
      "total-comments-made",
      dateTime,
    );
  }

  // Track average monthly comments
  trackAverageNumberOfMonthlyCommentsMade(
    user: any,
    totalMonthlyComments: number,
    dateTime?: Moment,
  ) {
    return this.trackMonthlyStatAverageByRef(
      `user-level-usage/${user.id}/posts/post-comments`,
      "average-comments-made",
      totalMonthlyComments,
      dateTime,
    );
  }

  // Track total safe-people invites received
  trackTotalNumberOfSafePeopleInvitesReceived(user: any) {
    // @Todo
  }

  // Track total safe-people invites
  trackTotalNumberOfSafePeopleInvites(user: any) {
    return this.incrementStatByRef(
      `user-level-usage/${user.id}/safe-people/total-invites-sent`,
    );
  }

  // Track total monthly safe-people invites
  trackTotalNumberOfMonthlySafePeopleInvites(user: any, dateTime?: Moment) {
    return this.trackMonthlyStatTotalByRef(
      `user-level-usage/${user.id}/safe-people`,
      "total-invites-sent",
      dateTime,
    );
  }

  // Track average monthly safe-people invites
  trackAverageNumberOfMonthlySafePeopleInvites(
    user: any,
    totalMonthlyInvitations: number,
    dateTime?: Moment,
  ) {
    return this.trackMonthlyStatAverageByRef(
      `user-level-usage/${user.id}/safe-people`,
      "average-invites-sent",
      totalMonthlyInvitations,
      dateTime,
    );
  }

  // Track total articles accessed
  trackTotalNumberOfArticlesAccessed(user: any): Promise<any> {
    return this.incrementStatByRef(
      `user-level-usage/${user.id}/articles/total-articles-accessed`,
    );
  }

  // Track total articles favourited
  trackTotalNumberOfArticlesFavourited(user: any): Promise<any> {
    return this.incrementStatByRef(
      `user-level-usage/${user.id}/articles/total-articles-favourited`,
    );
  }

  // Track relationship details
  trackRelationshipDetails(user: any, oldUser?: any): Promise<any> {
    return Promise.all([
      this.trackRankedListOfRelationshipHealth(user, oldUser),
      this.trackRankedListOfRelationshipValues(user, oldUser),
      this.trackRelationshipHealth(user, oldUser),
      this.trackRelationshipValues(user, oldUser),
    ]);
  }

  // Track relationship health
  trackRelationshipHealth(user: any, oldUser?: any): Promise<any> {
    let oldUserHealth = oldUser && oldUser.health ? oldUser.health : null;

    return this.trackRankedAttributeDiffStat(
      `user-level-usage/${user.id}/relationship-heath`,
      "health-level-changes",
      "level",
      user.health,
      oldUserHealth,
    );
  }

  // Track relationship values
  trackRelationshipValues(user: any, oldUser?: any): Promise<any> {
    let attributes = null;

    if (oldUser && oldUser.values) {
      attributes = differenceBy(
        values(oldUser.values),
        values(user.values),
        "id",
      );
    } else {
      attributes = values(user.values);
    }

    return this.trackRankedAttributeStats(
      `user-level-usage/${user.id}/relationship-values`,
      "value-changes",
      attributes,
    );
  }

  // Track post emotions
  trackPostEmotion(user: any, post: any): Promise<any> {
    if (!post.userId || !post.emotion) {
      return Promise.resolve(post);
    }

    return this.trackRankedAttributeStat(
      `user-level-usage/${post.userId}/posts`,
      "post-emotions",
      post.emotion,
    );
  }

  // Track post health
  trackPostHealth(user: any, post: any): Promise<any> {
    if (!post.health) {
      return Promise.resolve(post);
    }

    return this.trackRankedAttributeStat(
      `user-level-usage/${post.userId}/posts`,
      "post-health",
      post.health,
    );
  }

  // Track post values
  trackPostValues(user: any, post: any): Promise<any> {
    return this.trackRankedAttributeStats(
      `user-level-usage/${post.userId}/posts`,
      "post-values",
      post.values,
    );
  }

  // Track total monthly posts
  trackTotalNumberOfMonthlyPosts(user: any, dateTime?: Moment): Promise<any> {
    return this.trackMonthlyStatTotalByRef(
      `user-level-usage/${user.id}/posts`,
      "total-posts",
      dateTime,
    );
  }

  // Track average monthly posts
  trackAverageNumberOfMonthlyPosts(
    user: any,
    totalMonthlyPosts: number,
    dateTime: Moment,
  ): Promise<any> {
    return this.trackMonthlyStatAverageByRef(
      `user-level-usage/${user.id}/posts`,
      "average-posts",
      totalMonthlyPosts,
      dateTime,
    );
  }

  // Track total number of posts
  trackTotalNumberOfPosts(user: any): Promise<any> {
    return this.incrementStatByRef(
      `user-level-usage/${user.id}/posts/total-posts`,
    );
  }

  // Track total number of public posts
  trackTotalNumberOfPublicPosts(user: any): Promise<any> {
    return this.incrementStatByRef(
      `user-level-usage/${user.id}/posts/total-public-posts`,
    );
  }

  // Track total number of public posts
  trackTotalNumberOfInvitesPerSharedPosts(user: any): Promise<any> {
    return this.incrementStatByRef(
      `user-level-usage/${user.id}/posts/total-invites-sent-per-post`,
    );
  }

  // Track total monthly number of public posts
  trackTotalNumberOfMonthlyInvitesPerSharedPosts(
    user: any,
    dateTime: Moment,
  ): Promise<any> {
    return this.trackMonthlyStatTotalByRef(
      `user-level-usage/${user.id}/posts`,
      "total-invites-sent-per-post",
      dateTime,
    );
  }

  // Track total monthly number of public posts
  trackAverageNumberOfMonthlyInvitesPerSharedPosts(
    user: any,
    totalMonthlyInvitesPerPost: number,
    dateTime: Moment,
  ): Promise<any> {
    return this.trackMonthlyStatAverageByRef(
      `user-level-usage/${user.id}/posts`,
      "average-invites-sent-per-post",
      totalMonthlyInvitesPerPost,
      dateTime,
    );
  }

  // Track total monthly unresolved posts
  incrementTotalNumberOfMonthlyUnresolvedPosts(
    user: any,
    dateTime?: Moment,
  ): Promise<any> {
    return this.trackMonthlyStatTotalByRef(
      `user-level-usage/${user.id}/posts`,
      "posts-unresolved",
      dateTime,
    );
  }

  // Track total monthly unresolved posts
  incrementTotalNumberOfMonthlyResolvedPosts(
    user: any,
    dateTime?: Moment,
  ): Promise<any> {
    return this.trackMonthlyStatTotalByRef(
      `user-level-usage/${user.id}/posts`,
      "posts-resolved",
      dateTime,
    );
  }

  // Track average monthly unresolved posts
  incrementAverageNumberOfMonthlyResolvedPosts(
    user: any,
    totalMonthlyResolvedPosts: number,
    dateTime?: Moment,
  ): Promise<any> {
    return this.trackMonthlyStatAverageByRef(
      `user-level-usage/${user.id}/posts`,
      "posts-resolved",
      totalMonthlyResolvedPosts,
      dateTime,
    );
  }

  // Track total unresolved posts
  incrementTotalNumberOfUnresolvedPosts(user: any): Promise<any> {
    return this.incrementStatByRef(
      `user-level-usage/${user.id}/posts/posts-unresolved`,
    );
  }

  // Track total resolved posts
  incrementTotalNumberOfResolvedPosts(user: any): Promise<any> {
    return Promise.all([
      this.incrementStatByRef(
        `user-level-usage/${user.id}/posts/posts-resolved`,
      ),
      this.decrementStatByRef(
        `user-level-usage/${user.id}/posts/posts-unresolved`,
      ),
    ]);
  }

  // Track app access
  trackAppAccess(user: any, dateTime?: Moment): Promise<any> {
    return Promise.all([
      this.trackTotalNumberOfAccess(user),
      this.trackTotalNumberOfMonthlyAccess(user, dateTime).then(
        totalMonthlyAccess => {
          return this.trackAverageNumberOfMonthlyAccess(
            user,
            totalMonthlyAccess,
            dateTime,
          );
        },
      ),
    ]);
  }

  // Track total app access
  trackTotalNumberOfAccess(user: any): Promise<any> {
    return this.incrementStatByRef(
      `user-level-usage/${user.id}/access/total-times-accessed`,
    );
  }

  // Track total monthly app access
  trackTotalNumberOfMonthlyAccess(user: any, dateTime?: Moment): Promise<any> {
    return this.trackMonthlyStatTotalByRef(
      `user-level-usage/${user.id}/access`,
      "total-times-accessed",
      dateTime,
    );
  }

  // Track average monthly app access
  trackAverageNumberOfMonthlyAccess(
    user: any,
    totalMonthlyAccess: number,
    dateTime?: Moment,
  ): Promise<any> {
    return this.trackMonthlyStatAverageByRef(
      `user-level-usage/${user.id}/access`,
      "average-times-accessed",
      totalMonthlyAccess,
      dateTime,
    );
  }

  // Track elapsed time using the app
  trackTotalDurationOfMonthlyAccess(
    user: any,
    usageStartTime: any,
  ): Promise<any> {
    return new Promise(resolve => {
      if (!user) {
        return resolve(user);
      }

      let currentTime: any = moment();
      let currentDateString: string = currentTime.format("MM-YYYY");
      let totalElapsedDuration: string = null;

      this.getStatNodeByRef(
        `user-level-usage/${user.id}/access/${currentDateString}/duration`,
      )
        .transaction(totalDurationStr => {
          let elapsedTime: Moment = DateTime.getDifferenceAsUtc(
            usageStartTime,
            currentTime,
          );

          if (!totalDurationStr) {
            return DateTime.getFormattedDuration(elapsedTime);
          }

          let totalDuration = DateTime.getDurationAsUtc(totalDurationStr);
          totalDuration.add(elapsedTime.toObject());
          totalElapsedDuration = DateTime.getFormattedDuration(totalDuration);

          return totalElapsedDuration;
        })
        .then(() => resolve(totalElapsedDuration));
    });
  }

  // Tracking helpers
  trackMonthlyStatTotalByRef(
    baseRef: string,
    statNodeName: string,
    dateTime?: Moment,
  ) {
    if (!dateTime) {
      dateTime = moment();
    }
    let currentDate: string = dateTime.format("MM-YYYY");

    return this.incrementStatByRef(
      `${baseRef}/monthly-data/${currentDate}/${statNodeName}`,
    );
  }

  trackMonthlyStatAverageByRef(
    baseRef: string,
    statNodeName: string,
    totalStats: number,
    dateTime?: Moment,
  ) {
    if (!dateTime) {
      dateTime = moment();
    }
    let currentDate: string = dateTime.format("MM-YYYY");
    let monthlyAverageStat: number = null;

    return new Promise((resolve, reject) => {
      return this.getStatNodeByRef(
        `${baseRef}/monthly-data/${currentDate}/${statNodeName}`,
      )
        .transaction(averageStat => {
          if (!totalStats) {
            monthlyAverageStat = 0;

            return monthlyAverageStat;
          }

          if (!averageStat) {
            monthlyAverageStat = 1;

            return monthlyAverageStat;
          }

          let daysPassedInCurrentMonth = moment().diff(dateTime, "days");
          if (daysPassedInCurrentMonth > 0) {
            monthlyAverageStat = Math.ceil(
              totalStats / daysPassedInCurrentMonth,
            );
          } else {
            monthlyAverageStat = 1;
          }

          return monthlyAverageStat;
        })
        .then(() => resolve(monthlyAverageStat));
    });
  }

  trackRankedAttributeStats(
    baseRef: string,
    statNodeName: string,
    attributes: any[],
    enableTranslations: boolean = true,
    enableMonthlyStats: boolean = true,
  ) {
    if (!attributes) {
      return Promise.resolve(attributes);
    }

    let statPromises = [];
    each(attributes, attribute => {
      statPromises.push(
        this.trackRankedAttributeStat(
          baseRef,
          statNodeName,
          attribute,
          enableTranslations,
          enableMonthlyStats,
        ),
      );
    });

    return Promise.all(statPromises);
  }

  trackRankedAttributeStat(
    baseRef: string,
    statNodeName: string,
    attribute: any,
    enableTranslations: boolean = true,
    enableMonthlyStats: boolean = true,
  ): Promise<any> {
    if (!attribute) {
      return Promise.resolve(attribute);
    }

    return new Promise((resolve, reject) => {
      let currentDate: string = moment().format("MM-YYYY");

      this.getStatNodeByRef(`${baseRef}/${statNodeName}`)
        .transaction(totalStats => {
          let preparedAttr = this.getPreparedStatNode(
            attribute,
            totalStats,
            enableTranslations,
          );

          return preparedAttr;
        })
        .then(totalStats => {
          if (!enableMonthlyStats) {
            return resolve({
              totalStats: totalStats,
            });
          }

          this.getStatNodeByRef(
            `${baseRef}/monthly-data/${currentDate}/${statNodeName}`,
          )
            .transaction(monthlyStats => {
              return this.getPreparedStatNode(
                attribute,
                monthlyStats,
                enableTranslations,
              );
            })
            .then(monthlyStats =>
              resolve({
                totalStats: totalStats,
                monthlyStats: monthlyStats,
              }),
            );
        });
    });
  }

  trackRankedAttributeDiffStat(
    baseRef: string,
    statNodeName: string,
    statDiffPropertyName: string,
    newAttribute: any,
    oldAttribute: any,
    enableTranslations: boolean = true,
    enableMonthlyStats: boolean = true,
  ): Promise<any> {
    if (!newAttribute) {
      return Promise.resolve({
        newAttribute: newAttribute,
        oldAttribute: oldAttribute,
      });
    }

    if (
      oldAttribute &&
      oldAttribute[statDiffPropertyName] &&
      oldAttribute[statDiffPropertyName] === newAttribute[statDiffPropertyName]
    ) {
      return Promise.resolve({
        newAttribute: newAttribute,
        oldAttribute: oldAttribute,
      });
    }

    return this.trackRankedAttributeStat(
      baseRef,
      statNodeName,
      newAttribute,
      enableTranslations,
      enableMonthlyStats,
    );
  }

  private getPreparedStatNode(
    attribute: any,
    statNode: any,
    enableTranslations: boolean = true,
  ) {
    // Init final stat-node
    if (!statNode) {
      statNode = {};

      if (enableTranslations) {
        statNode.en = {};
        statNode.trm = {};
      }
    }

    // Handle translatable attributes
    if (enableTranslations) {
      if (
        !statNode.en[attribute["title_en"]] &&
        !statNode.trm[attribute["title_trm"]]
      ) {
        statNode.en[attribute["title_en"]] = 1;
        statNode.trm[attribute["title_trm"]] = 1;

        return statNode;
      }

      statNode.en[attribute["title_en"]] += 1;
      statNode.trm[attribute["title_trm"]] += 1;

      return statNode;
    }

    // Handle non-translatable attributes
    if (!statNode[attribute]) {
      statNode[attribute] = 1;

      return statNode;
    }

    statNode[attribute] += 1;

    return statNode;
  }

  // Stat Helpers
  getStatsJson(): Promise<any> {
    return new Promise(resolve => {
      this.af.database
        .object("stats")
        .$ref.once("value")
        .then((snapshot: any) => {
          let data = snapshot.val();
          let modifications = [
            this.modifyPostOutputNames(data["aggregate-level-usage"]["articles"]["total-accessed"]),
            this.modifyPostOutputNames(data["aggregate-level-usage"]["articles"]["total-favourited"])
          ];

          Promise.all(modifications).then(results => {
            data["aggregate-level-usage"]["articles"]["total-accessed"] = results[0];
            data["aggregate-level-usage"]["articles"]["total-favourited"] = results[1];
            console.log(data);
            resolve(data);
          });
        });
    });
  }

  modifyPostOutputNames(entries: any): Promise<any> {
    let result = {};
    let promises = [];
    for (let key in entries) {
      promises.push(this.af.database.object(`posts/${key}`)
        .$ref.once("value")
        .then(snapshot => {
          let post = snapshot.val();
          if (!post) {
            return;
          }
          result[post.title_en || post.title_trm] = entries[key];
        }));
    }
    return Promise.all(promises).then(() => result);
  }

  getStatNodeByRef(ref: string): firebase.database.Reference {
    return this.af.database.object(`stats/${ref}`).$ref;
  }

  updateStatByRef(ref: string, value: any): Promise<any> {
    if (value === null || typeof value === "undefined") {
      return null;
    }

    return new Promise((resolve, reject) => {
      this.getStatNodeByRef(ref)
        .transaction(() => value)
        .then(() => resolve(value));
    });
  }

  incrementStatByRef(ref: string, valueStep: number = 1): Promise<any> {
    return new Promise((resolve, reject) => {
      let updatedStat = null;

      this.getStatNodeByRef(ref)
        .transaction(stat => {
          updatedStat = stat ? stat + valueStep : valueStep;

          return updatedStat;
        })
        .then(() => resolve(updatedStat));
    });
  }

  decrementStatByRef(ref: string, valueStep: number = 1) {
    return new Promise((resolve, reject) => {
      let updatedStat = null;

      this.getStatNodeByRef(ref)
        .transaction(stat => {
          if (!stat) {
            return 0;
          }

          updatedStat = stat - valueStep;
          if (updatedStat < 0) {
            updatedStat = 0;
          }

          return updatedStat;
        })
        .then(() => resolve(updatedStat));
    });
  }
}



// WEBPACK FOOTER //
// ./src/providers/stats.ts